/**
 * Employee Incident Bulk Approval Types
 * Feature: bulk approval of pending marking incidents (ASIS-5249 / REXASIS-007)
 * Endpoint: PUT /v1/attendances/incidents/bulk-approval
 */

// Outcome discriminator constants — mirrors the individual approval outcome shape,
// one per incident included in the batch.
export const INCIDENT_BULK_APPROVAL_OUTCOME = {
  APPROVED: 'approved',
  ALREADY_RESOLVED: 'alreadyResolved',
} as const;

export type IncidentBulkApprovalOutcome =
  (typeof INCIDENT_BULK_APPROVAL_OUTCOME)[keyof typeof INCIDENT_BULK_APPROVAL_OUTCOME];

// Max length for the approver comment in the bulk modal (per design spec) — matches
// the backend's EmployeeIncidentConstants.MAX_BULK_APPROVER_COMMENT_LENGTH. Intentionally
// higher than the individual detail panel's local COMMENT_MAX_LENGTH (100): the bulk
// comment applies to every selected incident at once, so it allows more room.
export const INCIDENT_BULK_APPROVER_COMMENT_MAX_LENGTH = 500;

export interface LC_BulkApproveIncidentsIn {
  // Explicit-list mode: required unless `approveAll` is true.
  incidentIds?: string; // CSV, e.g. "1,2,3"
  approverComment: string; // REQUIRED
  // "Select all" mode (ASIS-5249): approves every Pendiente incident matching the filters
  // below, resolved server-side at execute time — `incidentIds` is ignored when true.
  approveAll?: boolean;
  dateFrom?: string | null; // YYYY-MM-DD
  dateTo?: string | null; // YYYY-MM-DD
  errorTypeId?: number | null;
  filterEmployeeId?: number | null;
  searchText?: string | null;
  excludeIncidentIds?: string; // CSV of incidents manually deselected while in select-all mode
}

export interface LC_BulkApproveIncidentItemOut {
  incidentId: number;
  outcome: IncidentBulkApprovalOutcome;
  newStatus: string;
  updatedDate: string | null; // ISO date-time
  // Only populated when outcome === 'alreadyResolved'.
  currentState: {
    status: string;
    approvedByFullName: string | null;
    approverComment: string | null;
    updatedDate: string | null;
  } | null;
}

export interface LC_BulkApproveIncidentsOut {
  items: LC_BulkApproveIncidentItemOut[];
  totalApproved: number;
  totalAlreadyResolved: number;
}
